import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import {
  Flame, Trophy, Crown, Shield, Star, BookOpen, Wrench, Rocket,
  ArrowRight, Users, Target, Zap, ChevronRight, Check, Lock,
  Vote, FileCode, Globe, Hash, Timer, Award, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_CONFIG = {
  Diamond: { gradient: 'from-cyan-400 to-blue-600', text: 'text-cyan-400', border: 'border-cyan-500/30', bg: 'bg-cyan-500/10', icon: Crown },
  Gold: { gradient: 'from-yellow-400 to-amber-600', text: 'text-yellow-400', border: 'border-yellow-500/30', bg: 'bg-yellow-500/10', icon: Trophy },
  Silver: { gradient: 'from-gray-300 to-gray-500', text: 'text-gray-300', border: 'border-gray-400/30', bg: 'bg-gray-400/10', icon: Shield },
};

const SAMPLE_QUESTIONS = [
  {
    q: 'How does the Velocity Score (Vs) prevent "Passive Parasites"?',
    options: ['Flat tax on large wallets', '7-day half-life decay on Activity metric', '100 governance votes/month'],
    correct: 1,
  },
  {
    q: 'During a Black Swan event, the Volatility Reserve primarily:',
    options: ['Pauses all trading', 'Buys back and burns tokens', 'Funds the Vulture Multiplier for liquidity providers'],
    correct: 2,
  },
  {
    q: 'What is the full Velocity Score formula?',
    options: ['Vs = C + T + A', 'Vs = (Ck*0.4) + (Tk\u00B2*0.35) + (Ak*0.25)', 'Vs = Total Staked / Total Supply'],
    correct: 1,
  },
];

function TierCard({ tier, delay }) {
  const cfg = TIER_CONFIG[tier.name];
  const Icon = cfg.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      viewport={{ once: true }}
      className={`relative overflow-hidden rounded-2xl border ${cfg.border} p-6 bg-[#07070f]`}
      data-testid={`tier-${tier.name.toLowerCase()}`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${cfg.gradient} opacity-[0.03]`} />
      <div className="relative">
        <div className={`w-12 h-12 rounded-xl ${cfg.bg} flex items-center justify-center mb-4`}>
          <Icon className={`w-6 h-6 ${cfg.text}`} />
        </div>
        <h3 className={`text-xl font-bold ${cfg.text}`}>{tier.name}</h3>
        <p className="text-sm text-white font-semibold mt-1">{tier.title}</p>
        <p className="text-xs text-gray-500 mt-0.5">Rank #{tier.min_rank} - #{tier.max_rank}</p>
        <div className="mt-4 space-y-2">
          {tier.perks.split('+').map((perk, i) => (
            <div key={i} className="flex items-start gap-2">
              <Check className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
              <span className="text-xs text-gray-300">{perk.trim()}</span>
            </div>
          ))}
        </div>
        {tier.multiplier > 1 && (
          <div className={`mt-4 inline-block px-3 py-1 rounded-full text-xs font-bold ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
            {tier.multiplier}x Vs Multiplier
          </div>
        )}
      </div>
    </motion.div>
  );
}

function QuizTeaser() {
  const [selected, setSelected] = useState({});
  const [revealed, setRevealedState] = useState({});

  const reveal = (qi) => {
    setRevealedState(prev => ({ ...prev, [qi]: true }));
  };

  return (
    <div className="space-y-4">
      {SAMPLE_QUESTIONS.map((q, qi) => (
        <motion.div
          key={qi}
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ delay: qi * 0.1 }}
          viewport={{ once: true }}
          className="p-5 rounded-xl bg-[#07070f] border border-white/5"
          data-testid={`sample-q-${qi}`}
        >
          <p className="text-sm text-white mb-3">
            <span className="text-purple-400 font-mono mr-2">Q{qi + 1}.</span>
            {q.q}
          </p>
          <div className="space-y-2">
            {q.options.map((opt, oi) => {
              const isCorrect = oi === q.correct;
              const isSelected = selected[qi] === oi;
              const isRevealed = revealed[qi];
              return (
                <button
                  key={oi}
                  onClick={() => { setSelected(prev => ({ ...prev, [qi]: oi })); reveal(qi); }}
                  disabled={isRevealed}
                  className={`w-full text-left p-3 rounded-lg text-xs border transition-all ${
                    isRevealed && isCorrect
                      ? 'bg-green-500/10 border-green-500/30 text-green-300'
                      : isRevealed && isSelected && !isCorrect
                      ? 'bg-red-500/10 border-red-500/30 text-red-300'
                      : isSelected
                      ? 'bg-purple-500/10 border-purple-500/30 text-white'
                      : 'bg-black/30 border-white/5 text-gray-400 hover:border-white/10 hover:text-gray-300'
                  }`}
                >
                  <span className="font-mono mr-2 text-gray-500">{String.fromCharCode(65 + oi)}.</span>
                  {opt}
                  {isRevealed && isCorrect && <Check className="w-3 h-3 text-green-400 inline ml-2" />}
                </button>
              );
            })}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function LeaderboardPreview({ entries }) {
  return (
    <div className="space-y-2">
      {entries.map((e, i) => {
        const cfg = e.tier ? TIER_CONFIG[e.tier.name] : null;
        const Icon = cfg?.icon || Star;
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            viewport={{ once: true }}
            className="flex items-center gap-3 p-3 rounded-lg bg-[#07070f] border border-white/5"
            data-testid={`lb-rank-${e.rank}`}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs ${
              e.rank <= 3 ? `bg-gradient-to-br ${cfg?.gradient || ''} text-white` : 'bg-white/5 text-gray-400'
            }`}>
              {e.rank <= 3 ? <Icon className="w-4 h-4" /> : e.rank}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{e.display_name}</p>
              <div className="flex gap-1.5 mt-0.5">
                {e.phase1_passed && <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-[8px] px-1 py-0">Scholar</Badge>}
                {e.phase2_complete && <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[8px] px-1 py-0">Builder</Badge>}
                {e.phase3_complete && <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 text-[8px] px-1 py-0">Catalyst</Badge>}
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-white">{e.vxp.toLocaleString()}</p>
              <p className="text-[9px] text-gray-500">vXP</p>
            </div>
            {cfg && <Badge className={`${cfg.bg} ${cfg.text} border ${cfg.border} text-[8px]`}>{e.tier.name}</Badge>}
          </motion.div>
        );
      })}
      {entries.length === 0 && (
        <div className="p-12 text-center">
          <Trophy className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No participants yet. Be the first Architect.</p>
        </div>
      )}
    </div>
  );
}

export default function QuestPortal() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/genesis-quest/overview`).then(r => r.json()),
      fetch(`${API}/api/genesis-quest/leaderboard?limit=20`).then(r => r.json()),
    ]).then(([ov, lb]) => {
      setOverview(ov);
      setLeaderboard(lb.leaderboard || []);
    }).catch(console.error);
  }, []);

  const phases = [
    {
      num: 1, title: 'The Scholar', icon: BookOpen, color: 'text-blue-400', bg: 'bg-blue-500/10',
      desc: 'Pass a 10-question technical quiz on Velocity Score dynamics, Anti-Fragile Reserve triggers, and Burn-to-Boost mechanics. Then survive a Black Swan simulation.',
      reward: '+500 vXP (Quiz) +200 vXP (Simulation)',
    },
    {
      num: 2, title: 'The Builder', icon: Wrench, color: 'text-green-400', bg: 'bg-green-500/10',
      desc: 'Participate in a mock governance vote with a 100-word rationale. Submit a documentation improvement or bug report to our open-source modules.',
      reward: '+200 vXP (Vote) +300-400 vXP (Contribution)',
    },
    {
      num: 3, title: 'The Catalyst', icon: Rocket, color: 'text-orange-400', bg: 'bg-orange-500/10',
      desc: 'Identify underserved trading pairs and submit a Listing Proposal. Earn referral bonuses only when your referrals pass the Scholar Quiz.',
      reward: '+250 vXP (Proposal) +150 vXP (Per Curated Referral)',
    },
  ];

  return (
    <div className="min-h-screen bg-[#030308] text-white">
      <Helmet>
        <title>The Kojiru Quest — Kojiru | Proof of Action Whitelist</title>
        <meta name="description" content="The era of Passive Staking is over. Earn Velocity XP through technical literacy, ecosystem contribution, and curated growth. Top 1,000 earn whitelist spots." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="The Kojiru Quest is LIVE — Kojiru" />
        <meta property="og:description" content="Proof of Action. Not Passive Staking. Pass the Scholar Quiz, build for the ecosystem, and earn your place among the top 1,000 Architects." />
        <meta property="og:image" content={`${API}/og-quest.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Kojiru Quest is LIVE — Kojiru" />
        <meta name="twitter:description" content="Don't just watch the market move. Be the reason it does. Earn Velocity XP and secure your whitelist spot. Top 1,000 only." />
        <meta name="twitter:image" content={`${API}/og-quest.png`} />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 via-transparent to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-[120px]" />

        <div className="max-w-5xl mx-auto px-6 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 px-4 py-1 text-xs mb-6">
              <Flame className="w-3 h-3 mr-1.5 inline" /> NOW LIVE
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="quest-portal-title">
              The Kojiru Quest
            </h1>
            <p className="text-base sm:text-lg text-gray-400 mt-4 max-w-2xl mx-auto leading-relaxed">
              The era of the "Passive Stake" is over. Prove your value through
              <span className="text-purple-400 font-semibold"> Proof of Action</span>.
              Top 1,000 contributors secure a whitelist spot.
            </p>

            {/* Live stats */}
            <div className="flex items-center justify-center gap-6 mt-8 flex-wrap">
              {[
                { label: 'Participants', value: overview?.total_participants || 0, icon: Users },
                { label: 'Scholars', value: overview?.phase_completions?.scholars || 0, icon: BookOpen },
                { label: 'Whitelist Slots', value: '1,000', icon: Star },
              ].map(s => (
                <div key={s.label} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/5">
                  <s.icon className="w-4 h-4 text-purple-400" />
                  <span className="text-sm font-bold text-white">{s.value}</span>
                  <span className="text-xs text-gray-500">{s.label}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-center gap-4 mt-8">
              <Button onClick={() => navigate('/auth')} size="lg"
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:brightness-110 gap-2 px-8"
                data-testid="join-quest-btn"
              >
                <Flame className="w-4 h-4" /> Join the Quest
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={() => document.getElementById('leaderboard')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-white/10 hover:bg-white/5 gap-2"
                data-testid="view-leaderboard-btn"
              >
                <Trophy className="w-4 h-4" /> View Leaderboard
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tiers */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <Badge variant="outline" className="text-xs text-gray-400 mb-3">REWARD TIERS</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold">Earn Your Place in the Ecosystem</h2>
            <p className="text-sm text-gray-500 mt-2">Ranked by Velocity XP (vXP). Quality over quantity.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6" data-testid="tiers-section">
            {overview?.tiers?.map((t, i) => (
              <TierCard key={t.name} tier={t} delay={i * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* Phases */}
      <section className="py-20 px-6 bg-[#050510]">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <Badge variant="outline" className="text-xs text-gray-400 mb-3">THE PATH</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold">Three Phases. One Goal.</h2>
            <p className="text-sm text-gray-500 mt-2">Each phase unlocks the next. No shortcuts.</p>
          </motion.div>

          <div className="space-y-4" data-testid="phases-section">
            {phases.map((p, i) => (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="flex gap-5 p-6 rounded-2xl bg-[#07070f] border border-white/5 hover:border-white/10 transition-colors"
              >
                <div className={`w-12 h-12 rounded-xl ${p.bg} flex items-center justify-center flex-shrink-0`}>
                  <p.icon className={`w-5 h-5 ${p.color}`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-bold text-white">Phase {p.num}: {p.title}</h3>
                  <p className="text-xs text-gray-400 mt-1 leading-relaxed">{p.desc}</p>
                  <div className="mt-3 flex items-center gap-2">
                    <Zap className="w-3 h-3 text-yellow-400" />
                    <span className="text-[11px] text-yellow-300 font-semibold">{p.reward}</span>
                  </div>
                </div>
                {i < phases.length - 1 && (
                  <div className="flex items-center">
                    <Lock className="w-4 h-4 text-gray-600" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quiz Teaser */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <Badge variant="outline" className="text-xs text-gray-400 mb-3">PROVE YOUR KNOWLEDGE</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold">Sample Scholar Questions</h2>
            <p className="text-sm text-gray-500 mt-2">Think you understand DeFi? Try these. The real quiz has 10 randomized questions and requires 100%.</p>
          </motion.div>
          <QuizTeaser />
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-8 text-center"
          >
            <Button onClick={() => navigate('/auth')} size="lg"
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:brightness-110 gap-2 px-8"
              data-testid="take-quiz-btn"
            >
              <BookOpen className="w-4 h-4" /> Take the Full Quiz
              <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Leaderboard */}
      <section id="leaderboard" className="py-20 px-6 bg-[#050510]">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <Badge variant="outline" className="text-xs text-gray-400 mb-3">LIVE RANKINGS</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold">Leaderboard</h2>
            <p className="text-sm text-gray-500 mt-2">
              {overview?.total_participants || 0} participants competing for 1,000 whitelist slots
            </p>
          </motion.div>

          {/* Tier legend */}
          <div className="flex items-center justify-center gap-4 mb-6 flex-wrap">
            {overview?.tiers?.map(t => {
              const cfg = TIER_CONFIG[t.name];
              return (
                <div key={t.name} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${cfg.bg} border ${cfg.border}`}>
                  <span className={`text-xs font-bold ${cfg.text}`}>{t.name}</span>
                  <span className="text-[10px] text-gray-400">#{t.min_rank}-{t.max_rank}</span>
                </div>
              );
            })}
          </div>

          <div data-testid="public-leaderboard">
            <LeaderboardPreview entries={leaderboard} />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-bold">
              Don't just watch the market move.
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">
                Be the reason it does.
              </span>
            </h2>
            <p className="text-sm text-gray-500 mt-4 max-w-xl mx-auto">
              The Kojiru Quest filters for the top 1% of DeFi users who value community and long-term utility.
              Mercenary capital need not apply.
            </p>
            <div className="flex items-center justify-center gap-4 mt-8">
              <Button onClick={() => navigate('/auth')} size="lg"
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:brightness-110 gap-2 px-8"
                data-testid="final-cta-btn"
              >
                <Flame className="w-4 h-4" /> Start the Quest
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
